import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { AssignService } from 'src/app/core/services/ticket/assign.service';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CategoriesModalComponent } from '@form-field-module/modal-windows/categories-modal/categories-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationsService } from 'angular2-notifications';
import { Subject } from 'rxjs';
import { CategoryChangeWarningStatusService } from '../../core/services/category-change-warning-status.service';
import { StatusChangeFailureStatus } from '../../core/enums/status-change-failure-status.enum';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CategoryFullNamePipe } from '../../shared/pipes/category-full-name.pipe';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';

@Component({
    selector: 'app-category-selector',
    templateUrl: './category-selector.component.html',
    styleUrls: ['./category-selector.component.scss'],
    imports: [NgClass, TooltipModule, TranslateModule, CategoryFullNamePipe],
})
export class CategorySelectorComponent implements OnDestroy {
    @Input() ticket: DetailedTicketDTO;

    @Output() needToAddTagsToCloseTicketEvent = new EventEmitter<boolean>();
    @Output() ticketStatusUpdated = new EventEmitter<{ status: TicketStatus; previousStatus: TicketStatus }>();
    @Output() updateTagsEvent = new EventEmitter<string>();

    isUpdatingCategory: boolean;
    isUpdatingTagsList: boolean;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public assignService: AssignService,
        private bsModalService: BsModalService,
        private translateService: TranslateService,
        private ticketService: TicketService,
        private notificationsService: NotificationsService,
        public categoryChangeWarningStatusService: CategoryChangeWarningStatusService,
    ) {}

    chooseCategory(selectedBranch, isTicketShouldBeClosedAfter = false, callback?: () => void, isHandled?: boolean) {
        this.isUpdatingCategory = true;
        this.isUpdatingTagsList = true;

        const categoryId = selectedBranch && selectedBranch.id ? selectedBranch.id : null;
        const previousStatus = this.ticket.status;

        this.ticketService
            .updateCategory(this.ticket.id, categoryId, isTicketShouldBeClosedAfter, isHandled)
            .pipe(
                finalize(() => {
                    this.isUpdatingCategory = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((result) => {
                if (categoryId) {
                    this.ticket.category = {
                        children: [],
                        label: selectedBranch.label,
                        name: selectedBranch.name,
                        id: selectedBranch.id,
                        parentData: selectedBranch.parentData,
                    };
                } else {
                    this.ticket.category = null;
                }

                if (result.status !== previousStatus) {
                    this.ticketStatusUpdated.emit({
                        status: result.status,
                        previousStatus,
                    });
                }

                this.assignService.loadUsersForAssign(this.ticket);
                this.updateTagsEvent.emit(categoryId);

                if (!result.userHasAccessToTicket) {
                    this.notificationsService.warn(
                        this.translateService.instant('Attention please'),
                        this.translateService.instant(
                            "You moved the ticket to a category you don't have access to. The ticket will stay assigned to you until you un-assign it.",
                        ),
                        {
                            timeOut: 10000,
                        },
                    );
                } else {
                    if (result.statusChangeOperationStatus === StatusChangeFailureStatus.tagRequired) {
                        this.needToAddTagsToCloseTicketEvent.emit(isHandled);
                    } else if (callback) {
                        callback();
                    }
                }
            });
    }

    checkCategoryChangeStatus() {
        this.categoryChangeWarningStatusService.checkCategoryChangeStatus(
            [this.ticket.id],
            this.openCategoriesModal.bind(this),
        );
    }

    openCategoriesModal(
        alertMessage?: string,
        isTicketShouldBeClosedAfter = false,
        callback?: () => void,
        isHandled?: boolean,
    ): void {
        if (this.ticket.assignee && this.ticket.assignee.id !== this.commonDataService.user.id) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Ticket is assigned to another user.'),
            );
            return;
        }
        const initialState = {
            isPrivate: false,
            onChangeCategory: (selectedBranch) => {
                this.chooseCategory(selectedBranch, isTicketShouldBeClosedAfter, callback, isHandled);
            },
            selectedCategoryIds: this.ticket.category ? [this.ticket.category.id] : [],
            isIgnoreUnselected: true,
            alertMessage,
        };

        this.bsModalService.show(CategoriesModalComponent, {
            initialState,
        });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
